import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../lib/utils';

// Base domains that are NOT subdomains (main site)
const MAIN_DOMAINS = [
  'woyof.com',
  'www.woyof.com',
  'localhost',
  '127.0.0.1',
];

// Preview/dev domains to skip subdomain detection
const SKIP_PATTERNS = [
  '.preview.emergentagent.com',
  '.vercel.app',
  '.netlify.app',
  '.railway.app',
];

/**
 * Detect if the current hostname is a storefront.
 *
 * Priority:
 * 1. *.woyof.com subdomains → extract subdomain directly
 * 2. Custom domain (anything else) → resolve via /api/domains/resolve/{hostname}
 * 3. Dev/preview environments → skip detection (render main app)
 *
 * Returns: { isStorefront, subdomain, loading }
 */
export function useStoreDomain() {
  const [state, setState] = useState({ isStorefront: false, subdomain: null, loading: true });

  useEffect(() => {
    const hostname = window.location.hostname;

    // Skip detection for dev/preview environments
    if (SKIP_PATTERNS.some(p => hostname.includes(p)) || hostname === 'localhost' || hostname === '127.0.0.1') {
      setState({ isStorefront: false, subdomain: null, loading: false });
      return;
    }

    // Check if it's a known main domain
    if (MAIN_DOMAINS.includes(hostname)) {
      setState({ isStorefront: false, subdomain: null, loading: false });
      return;
    }

    // Check if it's a subdomain of woyof.com (e.g., myshop.woyof.com)
    const woyofMatch = hostname.match(/^([a-z0-9-]+)\.woyof\.com$/i);
    if (woyofMatch && !['www', 'api', 'admin', 'app', 'mail'].includes(woyofMatch[1])) {
      setState({ isStorefront: true, subdomain: woyofMatch[1], loading: false });
      return;
    }

    // It's a custom domain — resolve it via the domains API
    const resolveHostname = async () => {
      try {
        const res = await axios.get(`${API}/domains/resolve/${hostname}`);
        setState({ isStorefront: true, subdomain: res.data.subdomain, loading: false });
      } catch {
        // Domain not found — try legacy lookup
        try {
          const res = await axios.get(`${API}/public/domain-lookup/${hostname}`);
          setState({ isStorefront: true, subdomain: res.data.subdomain, loading: false });
        } catch {
          // Truly not found — show main site
          setState({ isStorefront: false, subdomain: null, loading: false });
        }
      }
    };
    resolveHostname();
  }, []);

  return state;
}
